import React, { useState } from "react";

// design
const HEADER_BG = "#374151";
const CARD= { borderRadius: "8px", marginTop: "16px", boxShadow: "0 2px 6px rgba(0,0,0,0.10)" };
const HDR= { background: HEADER_BG, padding: "8px 14px", display: "flex", alignItems: "center", gap: "8px", borderRadius: "8px 8px 0 0" };
const TITLE= { margin: 0, color: "white", fontSize: "13px", fontWeight: "600" };
const SECTION= { borderLeft: "1px solid #cbd5e1", borderRight: "1px solid #cbd5e1" };
const FOOTER= { ...SECTION, borderBottom: "1px solid #cbd5e1", borderRadius: "0 0 8px 8px", padding: "5px 14px 6px", background: "#f9fafb", borderTop: "1px solid #e5e7eb", fontSize: "10px", color: "#6b7280", display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" };

const MODES = [
    { key: "counts",     label: "Counts",     desc: "Raw number of times row method beat column method." },
    { key: "winrate",    label: "Win rate",   desc: "Percentage of comparisons won by the row method." },
    { key: "dominance",  label: "Dominance",  desc: "Proportion of wins: 0 = never, 0.5 = equal, 1 = always." },
    { key: "difference", label: "Difference", desc: "Net wins: row wins vs column wins for the pair." },
];

export default function Heatmap({ data }) {
    const [mode, setMode] = useState("counts");

    // fallback
    if (!data || !data.twoAfcData || !data.twoAfcData.matrix) return null;

    const matrix = data.twoAfcData.matrix;
    const methods = Array.from(new Set(
        data.twoAfcData.participantChoices.flatMap(pc => [pc.valueA, pc.valueB])
    ));

    // compute color and text for one cell
    function computeCell(i, j) {
        if (i === j) return { bg: "#f1f5f9", text: "-", color: "#94a3b8", isDiag: true };

        const a = matrix[i][j];
        const b = matrix[j][i];
        const total = a + b;
        const dom = total > 0 ? a / total : 0.5;
        const diff = dom - 0.5;

        let r = 255, g = 255, bCol = 255;
        if (diff > 0) { const t = diff * 2; g = Math.round(255 - t * 150); bCol = Math.round(255 - t * 150); }
        else if (diff < 0) { const t = Math.abs(diff) * 2; r = Math.round(255 - t * 150); g = Math.round(255 - t * 150); }

        const bg = `rgb(${r},${g},${bCol})`;
        const brightness = (r * 299 + g * 587 + bCol * 114) / 1000;
        const color= brightness < 140 ? "white" : "#1f2937";

        // text displayed
        let text = a;
        if (mode === "winrate") text = total > 0 ? ((a / total) * 100).toFixed(1) + "%" : "–";
        if (mode === "dominance") text = dom.toFixed(2);
        if (mode === "difference") text = (a - b > 0 ? "+" : "") + (a - b);

        const winPct = total > 0 ? ((a / total) * 100).toFixed(1) : "–";
        const tip = `${methods[i]} vs ${methods[j]}\n${methods[i]} wins: ${a} / ${total} (${winPct}%)\n${methods[j]} wins: ${b} / ${total}`;
        return { bg, text, color, isDiag: false, tip };
    }

    const CELL_SIZE = "64px";
    const currentMode = MODES.find(m => m.key === mode);

    return (
        <div style={{ ...CARD, width: "fit-content" }}>

            <div style={HDR}>
                <h4 style={TITLE}>Pairwise Comparison Heatmap</h4>
                <div style={{ marginLeft: "auto", display: "flex", gap: "4px", alignItems: "center" }}>
                    {MODES.map(m => (
                        <button
                            key={m.key}
                            title={m.desc}
                            onClick={() => setMode(m.key)}
                            style={{
                                padding: "2px 8px",
                                fontSize: "10px",
                                fontWeight: mode === m.key ? "700" : "400",
                                border: "1px solid",
                                borderColor: mode === m.key ? "white" : "rgba(255,255,255,0.35)",
                                borderRadius: "4px",
                                background: mode === m.key ? "white" : "transparent",
                                color: mode === m.key ? "#374151" : "rgba(255,255,255,0.75)",
                                cursor: "pointer",
                                lineHeight: 1,
                                transition: "all 0.1s"
                            }}
                        >
                            {m.label}
                        </button>
                    ))}
                </div>
            </div>

            <div style={{ ...SECTION, padding: "4px 14px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb", fontSize: "10px", color: "#6b7280" }}>
                {currentMode?.desc}
            </div>

            <div style={{ ...SECTION, background: "white", padding: "12px 14px", overflowX: "auto" }}>
                <table style={{ borderCollapse: "separate", borderSpacing: "3px" }}>
                    <thead>
                        <tr>
                            <th style={{ width: "90px" }}>
                                <div style={{ fontSize: "9px", color: "#94a3b8", textAlign: "right", paddingRight: "6px", paddingBottom: "2px", lineHeight: 1.2 }}>
                                    row wins<br />↓ vs →
                                </div>
                            </th>
                            {methods.map((m, i) => (
                                <th key={i} style={{ width: CELL_SIZE, textAlign: "center", fontSize: "11px", fontWeight: "600", color: "#374151", padding: "4px 4px 6px", borderBottom: "2px solid #e5e7eb", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: CELL_SIZE }}>
                                    {m}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {methods.map((rowMethod, i) => (
                            <tr key={i}>
                                <th style={{ textAlign: "right", paddingRight: "10px", fontSize: "11px", fontWeight: "600", color: "#374151", whiteSpace: "nowrap" }}>
                                    {rowMethod}
                                </th>
                                {methods.map((_, j) => {
                                    const { bg, text, color, isDiag, tip } = computeCell(i, j);
                                    return (
                                        <td key={j} title={tip} style={{ background: bg, color, textAlign: "center", width: CELL_SIZE, height: "46px", fontSize: isDiag ? "16px" : "12px", fontWeight: isDiag ? "300" : "600", borderRadius: "4px", cursor: isDiag ? "default" : "help" }}>
                                            {text}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div style={FOOTER}>
                <span>Hover cells for details</span>
                <span>|</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                    <span style={{ width: "12px", height: "12px", background: "rgb(255,105,105)", display: "inline-block", borderRadius: "2px" }} />
                    red = higher win rate (&gt;50%)
                </span>
                <span>|</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                    <span style={{ width: "12px", height: "12px", background: "rgb(105,105,255)", display: "inline-block", borderRadius: "2px" }} />
                    blue = lower win rate (&lt;50%)
                </span>
                <span>|</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                    <span style={{ width: "12px", height: "12px", background: "#f1f5f9", border: "1px solid #e2e8f0", display: "inline-block", borderRadius: "2px" }} />
                    diagonal (same method)
                </span>
            </div>
        </div>
    );
}
